import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Home,
  BookOpen,
  MessageCircle,
  Users,
  Settings,
  LogOut,
  Target,
  Calendar,
  Sparkles,
  ChevronRight,
  Loader2,
  Menu,
  X,
  Brain,
  Beaker,
  Play,
  Award,
  Clock,
  Crown,
  AlertCircle,
  Route,
  Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { MinnowLogo, MinnowLogoFull } from '../components/MinnowLogo';

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout, fetchUser, skipTrial } = useAuth();
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [readinessScore, setReadinessScore] = useState(null);
  const [lastLesson, setLastLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showTrialEndedModal, setShowTrialEndedModal] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    loadDashboardData();
    // Check if trial just ended
    if (user?.trial_status?.trial_ended) {
      setShowTrialEndedModal(true);
    }
  }, []);

  // Calculate trial status
  const isOnTrial = user?.tier === 'trial';
  const trialDaysRemaining = user?.trial_status?.days_remaining || 0;
  const trialEnded = user?.trial_status?.trial_ended;
  const effectiveTier = user?.effective_tier || user?.tier || 'free';

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [planRes, scoreRes, lastLessonRes] = await Promise.all([
        api.getCurrentPlan(),
        api.getReadinessScore(),
        api.getLastLesson()
      ]);
      setWeeklyPlan(planRes.data.plan);
      setReadinessScore(scoreRes.data);
      setLastLesson(lastLessonRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePlan = async () => {
    setGeneratingPlan(true);
    try {
      const response = await api.generateWeeklyPlan(10);
      setWeeklyPlan(response.data);
      toast.success('Your weekly plan is ready!');
    } catch (error) {
      toast.error('Could not generate plan');
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleUpgrade = async (tier = 'basic') => {
    setUpgrading(true);
    try {
      const lookupKey = tier === 'pro' ? 'pro_monthly' : 'basic_monthly';
      const response = await api.createCheckout(lookupKey, window.location.origin);
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
        setUpgrading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
      setUpgrading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { icon: Home, label: t('nav.dashboard'), path: '/dashboard' },
    { icon: BookOpen, label: t('nav.curriculum'), path: '/curriculum' },
    { icon: Users, label: t('nav.rooms'), path: '/regional-rooms', locked: effectiveTier === 'free' },
    { icon: Brain, label: t('nav.minnow'), path: '/minnow' },
    { icon: Calendar, label: t('dashboard.weeklyFocus'), path: '/weekly-plan' },
    { icon: Beaker, label: t('nav.labs'), path: '/labs' },
    { icon: Trophy, label: t('nav.badges', 'Badges'), path: '/badges' },
    { icon: Award, label: t('nav.certification'), path: '/certification', locked: effectiveTier !== 'pro' },
    { icon: Settings, label: t('nav.settings'), path: '/settings' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="flex items-center justify-between h-16 px-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <MinnowLogo variant="blue" size={32} />
            <span className="font-bold text-foreground">Minnow</span>
          </Link>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 bottom-0 w-64 bg-card border-r border-border z-40
        transform transition-transform duration-300 lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="hidden lg:flex items-center gap-3 p-6 border-b border-border">
            <MinnowLogoFull variant="blue" logoSize={40} linkTo="/dashboard" />
          </div>
          <div className="lg:hidden h-16" />

          {/* User Info */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl gradient-ocean flex items-center justify-center">
                <span className="text-white font-semibold text-lg">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{user?.name}</p>
                <div className="flex items-center gap-2">
                  {isOnTrial ? (
                    <Badge variant="outline" className="text-xs border-mint text-mint">
                      <Clock className="w-3 h-3 mr-1" />
                      Trial: {trialDaysRemaining}d left
                    </Badge>
                  ) : (
                    <Badge variant="outline" className={`text-xs capitalize ${
                      effectiveTier === 'pro' ? 'border-coral text-coral' : 
                      effectiveTier === 'basic' ? 'border-ocean-blue text-ocean-blue' : 
                      'border-muted-foreground text-muted-foreground'
                    }`}>
                      {effectiveTier === 'pro' && <Crown className="w-3 h-3 mr-1" />}
                      {effectiveTier} tier
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            {/* Trial Banner */}
            {isOnTrial && trialDaysRemaining <= 3 && (
              <div className="mt-3 p-2 rounded-lg bg-coral/10 border border-coral/30">
                <p className="text-xs text-coral font-medium flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {t('tiers.trial')} - {trialDaysRemaining} {t('tiers.daysRemaining')}!
                </p>
                <Button 
                  size="sm" 
                  className="w-full mt-2 h-7 text-xs gradient-ocean"
                  onClick={() => setShowUpgradeModal(true)}
                >
                  {t('tiers.upgrade')}
                </Button>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const isLocked = item.locked;
              return (
                <Link
                  key={item.path}
                  to={isLocked ? '#' : item.path}
                  onClick={(e) => {
                    if (isLocked) {
                      e.preventDefault();
                      setShowUpgradeModal(true);
                    }
                    setSidebarOpen(false);
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive 
                      ? 'gradient-ocean text-white' 
                      : isLocked 
                        ? 'text-muted-foreground hover:bg-muted/50 cursor-pointer'
                        : 'text-foreground hover:bg-muted'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium flex-1">{item.label}</span>
                  {isLocked && (
                    <Badge variant="outline" className="text-xs">
                      {item.path === '/certification' ? t('tiers.pro') : t('tiers.basic')}+
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-border">
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3 text-coral hover:text-coral hover:bg-coral/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              {t('common.logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              {t('auth.welcomeBack').replace('!', '')}, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground">
              {weeklyPlan 
                ? `${t('dashboard.weeklyFocus')}: ${weeklyPlan.focus}`
                : t('dashboard.startLearning')
              }
            </p>
          </motion.div>

          {/* Main Grid */}

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Weekly Focus Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2"
            >
              <Card className="rounded-2xl border-0 shadow-lg overflow-hidden" data-testid="weekly-focus-card">
                <div className="gradient-ocean p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-6 h-6" />
                      <h2 className="text-xl font-semibold">{t('dashboard.weeklyFocus')}</h2>
                    </div>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={handleGeneratePlan}
                      disabled={generatingPlan}
                      className="bg-white/20 hover:bg-white/30 text-white border-0"
                      data-testid="generate-plan-btn"
                    >
                      {generatingPlan ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                      <span className="ml-2">{weeklyPlan ? t('common.regenerate') : t('common.generate')}</span>
                    </Button>
                  </div>
                  {weeklyPlan ? (
                    <p className="text-xl font-medium text-white/90">{weeklyPlan.focus}</p>
                  ) : (
                    <p className="text-white/70">{t('dashboard.startLearning')}</p>
                  )}
                </div>
                {weeklyPlan && (
                  <CardContent className="p-6">
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div 
                        className="p-4 rounded-xl bg-ocean-blue/5 border border-ocean-blue/20 cursor-pointer hover:bg-ocean-blue/10 transition-colors"
                        onClick={() => weeklyPlan.module && navigate(`/curriculum/${weeklyPlan.module}`)}
                        data-testid="weekly-module-link"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <BookOpen className="w-4 h-4 text-ocean-blue" />
                          <span className="text-sm font-medium text-ocean-blue">{t('curriculum.modules')}</span>
                        </div>
                        <p className="text-foreground font-medium">{weeklyPlan.module_title || weeklyPlan.module?.replace(/_/g, ' ')}</p>
                        <span className="text-xs text-ocean-blue flex items-center gap-1 mt-1">{t('common.start')} <ChevronRight className="w-3 h-3" /></span>
                      </div>
                      <div 
                        className={`p-4 rounded-xl bg-coral/5 border border-coral/20 ${weeklyPlan.lab ? 'cursor-pointer hover:bg-coral/10' : ''} transition-colors`}
                        onClick={() => weeklyPlan.lab && navigate('/labs')}
                        data-testid="weekly-lab-link"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Beaker className="w-4 h-4 text-coral" />
                          <span className="text-sm font-medium text-coral">{t('nav.labs')}</span>
                        </div>
                        <p className="text-foreground font-medium">{weeklyPlan.lab_title || weeklyPlan.lab || t('labs.noLabThisWeek')}</p>
                        {weeklyPlan.lab && <span className="text-xs text-coral flex items-center gap-1 mt-1">{t('common.start')} <ChevronRight className="w-3 h-3" /></span>}
                      </div>
                      <div className="p-4 rounded-xl bg-mint/30 border border-mint">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="w-4 h-4 text-ocean-blue" />
                          <span className="text-sm font-medium text-ocean-blue">{t('dashboard.dailyHabit')}</span>
                        </div>
                        <p className="text-foreground font-medium">{weeklyPlan.habit}</p>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            </motion.div>

            {/* Readiness Score */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="rounded-2xl border-0 shadow-lg h-full" data-testid="readiness-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-ocean-blue" />
                    {t('dashboard.readinessScore')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-6">
                    <div className="relative inline-flex items-center justify-center">
                      <svg className="w-28 h-28 transform -rotate-90">
                        <circle
                          cx="56"
                          cy="56"
                          r="48"
                          stroke="currentColor"
                          strokeWidth="10"
                          fill="none"
                          className="text-muted"
                        />
                        <circle
                          cx="56"
                          cy="56"
                          r="48"
                          stroke="currentColor"
                          strokeWidth="10"
                          fill="none"
                          strokeDasharray={`${(readinessScore?.score || 0) * 3.01} 301`}
                          className="text-ocean-blue transition-all duration-1000"
                        />
                      </svg>
                      <span className="absolute text-3xl font-bold text-foreground">
                        {readinessScore?.score || 0}
                      </span>
                    </div>
                    {/* Stage indicator */}
                    <p className="text-sm font-medium text-ocean-blue mt-2">
                      {readinessScore?.stage || t('dashboard.buildingFoundation')}
                    </p>
                  </div>
                  <div className="space-y-3">
                    {[
                      { key: 'clarity', label: 'Clarity', color: 'bg-ocean-blue' },
                      { key: 'traction', label: 'Traction', color: 'bg-sky-blue' },
                      { key: 'pitch_quality', label: 'Pitch', color: 'bg-coral' },
                      { key: 'execution_habits', label: 'Habits', color: 'bg-mint' }
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{item.label}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${item.color} rounded-full transition-all`}
                              style={{ width: `${(readinessScore?.[item.key] || 0) * 10}%` }}
                            />
                          </div>
                          <span className="font-medium w-4">{readinessScore?.[item.key] || 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground bg-muted/50 p-3 rounded-xl">
                    {readinessScore?.recommendation}
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Continue Learning */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="rounded-2xl border-0 shadow-lg" data-testid="continue-learning-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-sand-beige flex items-center justify-center">
                      <BookOpen className="w-7 h-7 text-ocean-blue" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground">{t('dashboard.continueWhere')}</p>
                      <h3 className="font-semibold text-foreground">
                        {lastLesson?.lesson_title || t('dashboard.welcomeMessage')}
                      </h3>
                      {lastLesson?.module_title && (
                        <p className="text-xs text-muted-foreground">{lastLesson.module_title}</p>
                      )}
                    </div>
                  </div>
                  <Link 
                    to={`/curriculum/${lastLesson?.module_id || 'module_0'}/lesson/${lastLesson?.lesson_id || '0.1'}`} 
                    data-testid="continue-lesson-btn"
                  >
                    <Button className="w-full rounded-xl gradient-ocean text-white">
                      <Play className="w-4 h-4 mr-2" />
                      {t('dashboard.continueLesson')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="lg:col-span-2"
            >
              <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.quickLinks')}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Link to="/minnow" data-testid="quick-minnow-btn">
                  <Card className="p-4 rounded-2xl border-0 shadow-md hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer">
                    <div className="w-12 h-12 rounded-xl gradient-ocean flex items-center justify-center mb-3">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <p className="font-medium text-foreground">{t('nav.minnow')}</p>
                    <p className="text-xs text-muted-foreground">{t('minnow.personalizedForYou')}</p>
                  </Card>
                </Link>
                <Link to="/curriculum" data-testid="quick-curriculum-btn">
                  <Card className="p-4 rounded-2xl border-0 shadow-md hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer">
                    <div className="w-12 h-12 rounded-xl bg-coral flex items-center justify-center mb-3">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <p className="font-medium text-foreground">{t('nav.curriculum')}</p>
                    <p className="text-xs text-muted-foreground">{t('curriculum.allModules')}</p>
                  </Card>
                </Link>
                <Link to="/labs" data-testid="quick-labs-btn">
                  <Card className="p-4 rounded-2xl border-0 shadow-md hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer">
                    <div className="w-12 h-12 rounded-xl bg-mint flex items-center justify-center mb-3">
                      <Beaker className="w-6 h-6 text-ocean-blue" />
                    </div>
                    <p className="font-medium text-foreground">{t('nav.labs')}</p>
                    <p className="text-xs text-muted-foreground">{t('labs.handsOn')}</p>
                  </Card>
                </Link>
                <Link to="/pathways" data-testid="quick-pathways-btn">
                  <Card className="p-4 rounded-2xl border-0 shadow-md hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer">
                    <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center mb-3">
                      <Route className="w-6 h-6 text-white" />
                    </div>
                    <p className="font-medium text-foreground">{t('nav.pathways')}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.learningTracks')}</p>
                  </Card>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-ocean-blue" />
              {t('dashboard.unlockFeatures')}
            </DialogTitle>
            <DialogDescription>
              {t('dashboard.choosePlan')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Basic Plan */}
            <div className={`p-4 rounded-xl border-2 ${effectiveTier === 'basic' ? 'border-ocean-blue bg-ocean-blue/5' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg">Basic</h3>
                <Badge className="bg-ocean-blue">$29/mo</Badge>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                <li>✓ All Layer 0 & 1 modules</li>
                <li>✓ Idea Lab & Pitch Lab</li>
                <li>✓ Regional Rooms access</li>
                <li>✓ Adaptive weekly plans</li>
              </ul>
              <Button 
                className="w-full gradient-ocean" 
                onClick={() => handleUpgrade('basic')}
                disabled={upgrading || effectiveTier === 'basic'}
              >
                {effectiveTier === 'basic' ? 'Current Plan' : 'Upgrade to Basic'}
              </Button>
            </div>
            
            {/* Pro Plan */}
            <div className={`p-4 rounded-xl border-2 ${effectiveTier === 'pro' ? 'border-coral bg-coral/5' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Crown className="w-5 h-5 text-coral" />
                  Pro
                </h3>
                <Badge className="bg-coral">$99/mo</Badge>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                <li>✓ Everything in Basic</li>
                <li>✓ Advanced modules (Fundraising, Scaling)</li>
                <li>✓ All labs including Investor Readiness</li>
                <li>✓ Certification system</li>
                <li>✓ Priority support</li>
              </ul>
              <Button 
                className="w-full bg-coral hover:bg-coral/90" 
                onClick={() => handleUpgrade('pro')}
                disabled={upgrading || effectiveTier === 'pro'}
              >
                {upgrading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {effectiveTier === 'pro' ? 'Current Plan' : 'Upgrade to Pro'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trial Ended Modal */}
      <Dialog open={showTrialEndedModal} onOpenChange={setShowTrialEndedModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-coral" />
              {t('dashboard.trialEnded')}
            </DialogTitle>
            <DialogDescription>
              {t('dashboard.trialEndedDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 rounded-xl bg-muted/50 mb-4">
              <p className="text-sm text-muted-foreground">{t('dashboard.whatYouLose')}</p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• {t('dashboard.loseModules')}</li>
                <li>• {t('dashboard.loseLabs')}</li>
                <li>• {t('dashboard.loseRooms')}</li>
                <li>• {t('dashboard.losePlans')}</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowTrialEndedModal(false)}>
              {t('dashboard.stayFree')}
            </Button>
            <Button 
              className="gradient-ocean" 
              onClick={() => {
                setShowTrialEndedModal(false);
                setShowUpgradeModal(true);
              }}
            >
              {t('dashboard.viewPlans')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
